import type { ImplementedLocale } from '~/lib/i18n/shared'

import { z } from 'zod'

import { resolveImplementedLocale } from '~/lib/i18n/shared'
import {
  getRouteLocalePrefix,
  isAuthRoutePath,
  isGlobalRoutePath,
  resolveLocalizedRouteTo,
} from './route-surface-policy'

const localOrigin = 'https://ballpeek.invalid'
const footballCorePath = /^\/(?:matches(?:\/[^/?#]+)?|teams-players|teams(?:\/[^?#]*)?|players(?:\/[^/?#]+)?|history\/matches\/[^/?#]+)\/?$/
const nestedNavigationKeys: Record<string, true> = { back: true, returnTo: true }
const maximumNestedNavigationDepth = 8
export function footballCoreLocale(localeInput?: string | null): ImplementedLocale {
  return resolveImplementedLocale(localeInput)
}

export function resolveFootballCoreRouteTo(
  to: string,
  localeInput?: string | null,
): string {
  return resolveLocalizedRouteTo(to, {
    locale: footballCoreLocale(localeInput),
    defaultLocale: 'en',
  })
}

export interface FootballCoreBrowserHistory {
  length: number
  referrer: string
  origin: string
}

export interface FootballCoreBrowserAdapter extends FootballCoreBrowserHistory {
  back: () => void
  assign: (href: string) => void
}

export type FootballCoreBackDestination
  = | { kind: 'history' }
    | { kind: 'target', href: string }

function hasUnsafeControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index)
    if (code <= 0x1F || code === 0x7F)
      return true
  }
  return false
}

export function isSafeRelativeNavigationTarget(value: string): boolean {
  if (
    value.length === 0
    || value.length > 4096
    || !value.startsWith('/')
    || value.startsWith('//')
    || value.includes('\\')
    || hasUnsafeControlCharacter(value)
  ) {
    return false
  }

  try {
    return new URL(value, localOrigin).origin === localOrigin
  }
  catch {
    return false
  }
}

export const relativeNavigationTargetSchema = z.string().refine(
  isSafeRelativeNavigationTarget,
  'Expected a same-origin relative navigation target',
)

function localizedPathname(localeInput: string | null | undefined, pathname: string): string {
  const locale = resolveImplementedLocale(localeInput)
  const routePrefix = getRouteLocalePrefix(pathname)
  let path = routePrefix?.pathWithoutLocale ?? pathname
  if (!path.startsWith('/'))
    path = `/${path}`

  if (locale === 'en' || (isGlobalRoutePath(path) && !isAuthRoutePath(path)))
    return path
  return `/zh-CN${path === '/' ? '' : path}`
}

function appendQuery(
  target: URLSearchParams,
  query: URLSearchParams | Record<string, string | number | null | undefined>,
): void {
  if (query instanceof URLSearchParams) {
    for (const [name, value] of query)
      target.append(name, value)
    return
  }

  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined && value !== null)
      target.append(name, String(value))
  }
}

export function footballCoreHref(
  localeInput: string | null | undefined,
  href: string,
  options: {
    back?: string
    query?: URLSearchParams | Record<string, string | number | null | undefined>
    hash?: string
  } = {},
): string {
  if (!isSafeRelativeNavigationTarget(href))
    throw new TypeError('Expected a same-origin relative navigation target')

  const parsed = new URL(href, localOrigin)
  if (options.query)
    appendQuery(parsed.searchParams, options.query)
  if (options.back !== undefined) {
    if (!isSafeRelativeNavigationTarget(options.back))
      throw new TypeError('Expected a safe nested Back target')
    parsed.searchParams.set('back', options.back)
  }
  if (options.hash !== undefined)
    parsed.hash = options.hash.length === 0 ? '' : `#${options.hash.replace(/^#/, '')}`

  return `${localizedPathname(localeInput, parsed.pathname)}${parsed.search}${parsed.hash}`
}

function localizeNestedNavigationTarget(
  localeInput: string | null | undefined,
  value: string,
  depth: number,
): string {
  if (depth > maximumNestedNavigationDepth || !isSafeRelativeNavigationTarget(value))
    return value

  const parsed = new URL(value, localOrigin)
  const query = new URLSearchParams()
  for (const [name, nestedValue] of parsed.searchParams) {
    query.append(
      name,
      name in nestedNavigationKeys
        ? localizeNestedNavigationTarget(localeInput, nestedValue, depth + 1)
        : nestedValue,
    )
  }
  const serialized = query.toString()
  return `${localizedPathname(localeInput, parsed.pathname)}${serialized ? `?${serialized}` : ''}${parsed.hash}`
}

export function buildFootballCoreLocalePath(
  localeInput: string | null | undefined,
  pathname: string,
  search: string,
  hash: string,
): string {
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const localizedQuery = new URLSearchParams()
  for (const [name, value] of query) {
    localizedQuery.append(
      name,
      name in nestedNavigationKeys
        ? localizeNestedNavigationTarget(localeInput, value, 0)
        : value,
    )
  }
  const serialized = localizedQuery.toString()
  const fragment = hash.length === 0 ? '' : `#${hash.replace(/^#/, '')}`
  return `${localizedPathname(localeInput, pathname)}${serialized ? `?${serialized}` : ''}${fragment}`
}

export function isSafeFootballCoreBackTarget(value: string, locale: ImplementedLocale): boolean {
  if (!isSafeRelativeNavigationTarget(value))
    return false

  const parsed = new URL(value, localOrigin)
  const localized = parsed.pathname === '/zh-CN' || parsed.pathname.startsWith('/zh-CN/')
  if ((locale === 'zh-CN') !== localized)
    return false

  const path = localized ? parsed.pathname.slice('/zh-CN'.length) : parsed.pathname
  return footballCorePath.test(path)
}

export function safeFootballCoreBackTarget(
  value: string | undefined,
  fallback: string,
  locale: ImplementedLocale,
): string {
  return value !== undefined && isSafeFootballCoreBackTarget(value, locale) ? value : fallback
}

export function footballCoreBackTargetSchema(locale: ImplementedLocale) {
  return z.string().refine(
    value => isSafeFootballCoreBackTarget(value, locale),
    'Expected a same-locale relative Football Core path',
  )
}

export function currentFootballCoreLocation(location: {
  pathname: string
  searchStr?: string
  hash?: string
}): string {
  const search = location.searchStr
    ? `?${location.searchStr.replace(/^\?/, '')}`
    : ''
  const hash = location.hash
    ? `#${location.hash.replace(/^#/, '')}`
    : ''
  return `${location.pathname}${search}${hash}`
}

function canUseFootballCoreBrowserHistory(
  browserHistory: FootballCoreBrowserHistory | undefined,
  locale: ImplementedLocale,
): boolean {
  if (!browserHistory || browserHistory.length <= 1 || browserHistory.referrer.length === 0)
    return false

  try {
    const referrer = new URL(browserHistory.referrer)
    if (referrer.origin !== browserHistory.origin)
      return false
    return isSafeFootballCoreBackTarget(
      `${referrer.pathname}${referrer.search}${referrer.hash}`,
      locale,
    )
  }
  catch {
    return false
  }
}

export function resolveFootballCoreBack(input: {
  explicitTarget?: string
  fallback: string
  locale: ImplementedLocale
  browserHistory?: FootballCoreBrowserHistory
}): FootballCoreBackDestination {
  if (!isSafeFootballCoreBackTarget(input.fallback, input.locale))
    throw new TypeError('Expected a safe same-locale Football Core fallback')

  if (input.explicitTarget !== undefined) {
    return {
      kind: 'target',
      href: isSafeFootballCoreBackTarget(input.explicitTarget, input.locale)
        ? input.explicitTarget
        : input.fallback,
    }
  }

  return canUseFootballCoreBrowserHistory(input.browserHistory, input.locale)
    ? { kind: 'history' }
    : { kind: 'target', href: input.fallback }
}

export function navigateFootballCoreBack(
  input: {
    explicitTarget?: string
    fallback: string
    locale: ImplementedLocale
  },
  browser: FootballCoreBrowserAdapter,
): void {
  const destination = resolveFootballCoreBack({
    ...input,
    browserHistory: browser,
  })
  if (destination.kind === 'history')
    browser.back()
  else
    browser.assign(destination.href)
}
